import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

export default function LoginScreen() {
  const nav = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  async function enter() {
    await qc.invalidateQueries({ queryKey: ['me'] });
    // AuthGate рендерит LoginScreen «на месте» для дип-линков — тогда pathname
    // остаётся целевым URL. Явные визиты /login|/join после входа шлём на главную.
    const { pathname } = location;
    const dest = pathname === '/login' || pathname === '/join' ? '/' : pathname;
    nav(dest, { replace: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await authApi.login(login.trim(), password);
      await enter();
    } catch (e) {
      const m = (e as Error).message;
      setErr(m === 'throttled' ? 'Слишком много попыток — подождите минуту' : 'Неверный логин или пароль');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let botUsername = '';
      try {
        botUsername = (await authApi.config()).botUsername;
      } catch {
        return;
      }
      if (cancelled || !botUsername || !widgetRef.current) return;
      (window as unknown as { onTelegramAuth: (u: Record<string, unknown>) => void }).onTelegramAuth =
        async (user) => {
          try {
            await authApi.telegram(user);
            await enter();
          } catch {
            setErr('Не удалось войти через Telegram');
          }
        };
      const s = document.createElement('script');
      s.src = 'https://telegram.org/js/telegram-widget.js?22';
      s.async = true;
      s.setAttribute('data-telegram-login', botUsername);
      s.setAttribute('data-size', 'large');
      s.setAttribute('data-onauth', 'onTelegramAuth(user)');
      s.setAttribute('data-request-access', 'write');
      widgetRef.current.appendChild(s);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-tg-bg">
      <Card className="w-full max-w-sm space-y-4">
        <h1 className="text-xl font-semibold text-center">Вход в обучение Amrita</h1>
        <form onSubmit={submit} className="space-y-3">
          <input
            className="w-full rounded-lg border border-tg-secondaryBg bg-tg-bg px-3 py-2"
            placeholder="Логин"
            autoComplete="username"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
          />
          <input
            type="password"
            className="w-full rounded-lg border border-tg-secondaryBg bg-tg-bg px-3 py-2"
            placeholder="Пароль"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {err && <div className="text-sm text-red-500">{err}</div>}
          <Button type="submit" disabled={busy || !login || !password} className="w-full">
            {busy ? 'Вход…' : 'Войти'}
          </Button>
        </form>
        <div className="text-center text-xs text-tg-hint">или</div>
        <div ref={widgetRef} className="flex justify-center" />
        <p className="text-xs text-tg-hint text-center">
          Нет доступа? Попросите у администратора ссылку-приглашение.
        </p>
      </Card>
    </div>
  );
}
